// BRIEF 86 PHASE 2, STEP 9c (run by brief 90 §5) — the REMINDER | LEVEL row,
// once, for both entity modals.
//
// The last shared block in the pair above eight lines that is not the ruled
// per-modal set. Brief 87 §7 T1 put the two controls in the same order on both
// sides ("Event reads REMINDER then LEVEL; Task read LEVEL then REMINDER. TASK
// CHANGES"), which is what left two identical rows in two files.
//
// ONE DIFFERENCE, and it was value-identical by construction: the Level select's
// onChange read `updateField("priority", v)` in the event modal and
// `setForm((p) => ({ ...p, priority: v }))` in the task modal — and
// `App.tsx:6351` defines `updateField` as exactly that setForm. Both modals are
// handed the same `updateField` from the same App, so this is one expression
// spelled two ways, not two behaviours. The row takes `updateField`.
//
// NOTHING IS PARAMETERISED. There is no `kind` here: a reminder is a reminder
// and a level is a level on both entities, and neither control reads an id.
//
// IT RENDERS THE TWO FIELDS AND NOT THEIR ROW, deliberately. The event modal
// gives them a `field-row` of their own; the task modal has them at the tail of
// the row that opens with Due Date. A wrapper here would move one of the two
// out of its grid — which is a layout change, and Phase 2 makes none.
import { REMINDER_OPTS } from "../../utils";
import { resolveLongLabel } from "../../lib/taxonomyHelpers";
import FieldSelect from "../FieldSelect"; // 154B §2
import type { TaxEntry, TaxList, TFn } from "../../lib/taxonomyTypes";
import type { makeModalFields } from "../modalFields";
import type { FieldSelectOption } from "../FieldSelect";

type Vocab = ReturnType<typeof makeModalFields>;
type ReminderOpt = [string, string] | { v: string; l: string };

type Props = {
  // The select-row builder both modals already hold (modalFields' `S`).
  S: Vocab["S"];
  form: { priority?: string | null; [k: string]: unknown };
  updateField: (k: string, v: unknown) => void;
  // The firm's reminder ladder, falling back to the built-in list.
  // Both hosts receive these in the app's own loose shapes; the row narrows
  // where it reads them, exactly as the inline copies did.
  reminderOptsList?: readonly FieldSelectOption[] | null;
  itemLevelEntries?: TaxList;
  t: TFn;
};

export default function ReminderLevelFields({
  S,
  form,
  updateField,
  reminderOptsList,
  itemLevelEntries,
  t,
}: Props) {
  return (
    <>
      {S(
        "Reminder",
        "reminder",
        // The firm's ladder arrives as objects and the built-in one as tuples;
        // both hosts normalised to tuples here, and the union is spelled out
        // rather than inferred so `.map` sees one element type.
        ((reminderOptsList && reminderOptsList.length
          ? reminderOptsList
          : REMINDER_OPTS) as readonly ReminderOpt[]).map((o: ReminderOpt) =>
          Array.isArray(o) ? o : [o.v, o.l],
        ),
      )}
      {/* 154B §2 — FieldSelect's own shape; the 110 §3a divider branch becomes
          the primitive's 108 §6b row, which is the same rule drawn by the menu
          instead of by an em-dash string. */}
      <FieldSelect
        label={t("field.priority", { fallback: "Level" })}
        value={form.priority || ""}
        onChange={(v) => updateField("priority", v || null)}
        options={[
          ["", t("common.unset_dashed", { fallback: "— Unset —" })] as const,
          ...((itemLevelEntries || []) as readonly TaxEntry[]).map(
            (e2: TaxEntry) =>
              [String(e2.code ?? ""), resolveLongLabel(e2) || ""] as const,
          ),
        ]}
      />
    </>
  );
}
